/**
 * Content Translate Sub-page
 *
 * Renders post/page translation interface within a tab panel.
 * Mirrors the StringTranslatePage layout for a unified UI across all tabs.
 *
 * @package MultilingualPressZone
 * @since 1.2.0
 */

import { el, __, debounce, clear } from '../utils/dom.js';
import API from '../utils/api.js';
import Table from '../components/Table.js';
import Badge from '../components/Badge.js';
import { Button } from '../components/Button.js';
import Modal from '../components/Modal.js';
import Toast from '../components/Toast.js';
import { Skeleton } from '../components/Skeleton.js';
import { FilterBar, FilterGroup } from '../components/FilterBar.js';
import { showTranslatePromoModal } from '../utils/promo-modal.js';
import ProgressBar from '../components/ProgressBar.js';

export default class ContentTranslatePage {
    /**
     * @param {HTMLElement} container - Tab content container
     * @param {string} postType - 'post' or 'page'
     * @param {Array} languages - Pre-loaded active languages
     */
    constructor(container, postType, languages) {
        this.container = container;
        this.postType = postType;
        this.languages = languages || [];
        this.items = [];
        this.filters = { search: '', status: '', language: '' };
        this.currentPage = 1;
        this.perPage = 50;
        this.totalPages = 1;
        this.total = 0;
        this.modal = null;
        this.editModalActiveTab = null;
        this.editContentData = null;
        this._destroyed = false;
        this._generating = false;
        this._generatingLang = null;
        this._saving = false;
        this._characterEstimates = {};
        this.tableContainer = null;
        this.paginationContainer = null;
        this.filterCounts = { status: {}, language: {} };
        this.translateAllInProgress = false;
        this.translateAllProgress = { total: 0, completed: 0, failed: 0, currentLang: null, results: [] };
        this.translateAllProgressBar = null;

        this.table = new Table({
            columns: [
                { label: __('Title', 'multilingual-press-zone'), key: 'title', width: '35%' },
                { label: __('Status', 'multilingual-press-zone'), key: 'status', width: '15%' },
                { label: __('Progress', 'multilingual-press-zone'), key: 'progress', width: '15%' },
                { label: __('Last Updated', 'multilingual-press-zone'), key: 'updated', width: '20%' },
                { label: __('Actions', 'multilingual-press-zone'), key: 'actions', width: '15%' }
            ],
            className: 'presszone-multilingual-table presszone-multilingual-table--legacy mpz-st-table',
            emptyMessage: this.postType === 'page'
                ? __('No pages found.', 'multilingual-press-zone')
                : __('No posts found.', 'multilingual-press-zone')
        });

        this.handleFilterChange = debounce(this._onFilterChange.bind(this), 300);
    }

    async render() {
        if (this._destroyed) return;

        clear(this.container);

        // Show skeleton while loading
        const skeletonContainer = el('div', { class: 'mpz-st-skeleton-container' });
        skeletonContainer.appendChild(Skeleton('row', 8));
        this.container.appendChild(skeletonContainer);

        if (this._destroyed) return;

        // Replace skeleton with real UI
        clear(this.container);

        // Filters
        const filters = this.renderFilters();
        this.container.appendChild(filters);

        // Table container
        this.tableContainer = el('div', {
            class: 'mpz-table-container presszone-multilingual-table-container',
            id: `mpz-ct-table-container-${this.postType}`
        });
        this.container.appendChild(this.tableContainer);

        // Pagination container
        this.paginationContainer = el('div', {
            class: 'mpz-pagination-container',
            id: `mpz-ct-pagination-${this.postType}`
        });
        this.container.appendChild(this.paginationContainer);

        // Load content
        await this.loadContent();
    }

    async loadContent() {
        if (this._destroyed || !this.tableContainer) return;

        // Show loading
        clear(this.tableContainer);
        this.tableContainer.appendChild(Skeleton('row', 8));

        try {
            const params = {
                postType: this.postType,
                page: this.currentPage,
                per_page: this.perPage,
            };
            if (this.filters.search) params.search = this.filters.search;
            if (this.filters.status) params.status = this.filters.status;
            if (this.filters.language) params.language = this.filters.language;

            const data = await API.get('/translations/content', params);

            if (this._destroyed) return;

            this.items = data.data || [];
            this.totalPages = data.pagination?.pages || 1;
            this.currentPage = data.pagination?.page || 1;
            this.total = data.pagination?.total || 0;

            if (data.filter_counts) {
                this.filterCounts = data.filter_counts;
                this.updateFilterCounts();
            }

            this.renderTable();
            this.renderPagination();
        } catch (error) {
            if (this._destroyed) return;
            Toast.error(error?.message || __('Failed to load content', 'multilingual-press-zone'));
            clear(this.tableContainer);
        }
    }

    renderFilters() {
        const sc = this.filterCounts.status || {};
        const lc = this.filterCounts.language || {};
        const totalPosts = (sc.translated || 0) + (sc.partial || 0) + (sc.untranslated || 0);

        const statusOptions = [
            el('option', { value: '' }, __('All Statuses', 'multilingual-press-zone') + ` (${totalPosts})`),
            el('option', { value: 'translated' }, __('Fully Translated', 'multilingual-press-zone') + ` (${sc.translated || 0})`),
            el('option', { value: 'partial' }, __('Partially Translated', 'multilingual-press-zone') + ` (${sc.partial || 0})`),
            el('option', { value: 'untranslated' }, __('Not Translated', 'multilingual-press-zone') + ` (${sc.untranslated || 0})`),
        ];

        const totalLangPosts = Object.values(lc).reduce((sum, n) => sum + n, 0);
        const languageOptions = [
            el('option', { value: '' }, __('All Languages', 'multilingual-press-zone') + ` (${totalLangPosts})`),
            ...this.languages.map(lang =>
                el('option', { value: lang.code }, `${lang.name || lang.code} (${lc[lang.code] || 0})`)
            )
        ];

        return FilterBar({
            groups: [
                FilterGroup({
                    label: __('Search', 'multilingual-press-zone'),
                    forId: `mpz-ct-search-${this.postType}`,
                    className: 'mpz-filter-group--search',
                    control: el('input', {
                        type: 'text',
                        id: `mpz-ct-search-${this.postType}`,
                        class: 'mpz-filter-input presszone-multilingual-input--settings presszone-multilingual-input--padding-standard',
                        placeholder: this.postType === 'page'
                            ? __('Search pages...', 'multilingual-press-zone')
                            : __('Search posts...', 'multilingual-press-zone'),
                        value: this.filters.search,
                        oninput: (e) => {
                            this.filters.search = e.target.value;
                            this.handleFilterChange();
                        }
                    })
                }),
                FilterGroup({
                    label: __('Status', 'multilingual-press-zone'),
                    forId: `mpz-ct-status-${this.postType}`,
                    control: el('select', {
                        id: `mpz-ct-status-${this.postType}`,
                        class: 'mpz-filter-select presszone-multilingual-select--settings presszone-multilingual-select--padding-standard',
                        value: this.filters.status,
                        onchange: (e) => {
                            this.filters.status = e.target.value;
                            this.handleFilterChange();
                        }
                    }, ...statusOptions)
                }),
                FilterGroup({
                    label: __('Language', 'multilingual-press-zone'),
                    forId: `mpz-ct-language-${this.postType}`,
                    control: el('select', {
                        id: `mpz-ct-language-${this.postType}`,
                        class: 'mpz-filter-select presszone-multilingual-select--settings presszone-multilingual-select--padding-standard',
                        value: this.filters.language,
                        onchange: (e) => {
                            this.filters.language = e.target.value;
                            this.handleFilterChange();
                        }
                    }, ...languageOptions)
                }),
                el('div', { class: 'mpz-filter-group mpz-filter-group--actions' },
                    Button({
                        label: __('Clear Filters', 'multilingual-press-zone'),
                        variant: 'ghost',
                        size: 'sm',
                        onClick: () => this.clearFilters()
                    })
                )
            ]
        });
    }

    updateFilterCounts() {
        const sc = this.filterCounts.status || {};
        const lc = this.filterCounts.language || {};
        const totalPosts = (sc.translated || 0) + (sc.partial || 0) + (sc.untranslated || 0);

        const statusSelect = document.getElementById(`mpz-ct-status-${this.postType}`);
        if (statusSelect) {
            const labels = [
                __('All Statuses', 'multilingual-press-zone') + ` (${totalPosts})`,
                __('Fully Translated', 'multilingual-press-zone') + ` (${sc.translated || 0})`,
                __('Partially Translated', 'multilingual-press-zone') + ` (${sc.partial || 0})`,
                __('Not Translated', 'multilingual-press-zone') + ` (${sc.untranslated || 0})`,
            ];
            Array.from(statusSelect.options).forEach((opt, i) => {
                if (i < labels.length) opt.textContent = labels[i];
            });
        }

        const langSelect = document.getElementById(`mpz-ct-language-${this.postType}`);
        if (langSelect) {
            const totalLangPosts = Object.values(lc).reduce((sum, n) => sum + n, 0);
            const firstOpt = langSelect.options[0];
            if (firstOpt) firstOpt.textContent = __('All Languages', 'multilingual-press-zone') + ` (${totalLangPosts})`;
            this.languages.forEach((lang, i) => {
                const opt = langSelect.options[i + 1];
                if (opt) opt.textContent = `${lang.name || lang.code} (${lc[lang.code] || 0})`;
            });
        }
    }

    clearFilters() {
        this.filters = { search: '', status: '', language: '' };

        const searchInput = document.getElementById(`mpz-ct-search-${this.postType}`);
        const statusSelect = document.getElementById(`mpz-ct-status-${this.postType}`);
        const languageSelect = document.getElementById(`mpz-ct-language-${this.postType}`);

        if (searchInput) searchInput.value = '';
        if (statusSelect) statusSelect.value = '';
        if (languageSelect) languageSelect.value = '';

        this._onFilterChange();
    }

    renderTable() {
        if (!this.tableContainer) return;
        clear(this.tableContainer);

        if (this.items.length === 0) {
            this.tableContainer.appendChild(
                el('div', { class: 'presszone-multilingual-empty-state' },
                    el('p', {}, this.postType === 'page'
                        ? __('No pages found matching your filters.', 'multilingual-press-zone')
                        : __('No posts found matching your filters.', 'multilingual-press-zone')
                    )
                )
            );
            return;
        }

        const tableEl = this.table.render(this.items, (item) => this.renderRow(item));
        this.tableContainer.appendChild(tableEl);
    }

    renderRow(item) {
        const { translated, total } = item.translations || { translated: 0, total: 0 };
        const progressPct = total > 0 ? Math.round((translated / total) * 100) : 0;
        let badgeVariant = 'default';
        if (progressPct === 100) badgeVariant = 'success';
        else if (progressPct > 0) badgeVariant = 'warning';

        // Determine overall status
        let statusText = __('Not Translated', 'multilingual-press-zone');
        let statusVariant = 'default';
        if (progressPct === 100) {
            statusText = __('Translated', 'multilingual-press-zone');
            statusVariant = 'success';
        } else if (progressPct > 0) {
            statusText = __('Partial', 'multilingual-press-zone');
            statusVariant = 'warning';
        }

        const row = el('tr', {
            class: 'mpz-st-table-row',
            'data-content-id': item.id,
            onclick: () => this.openEditModal(item)
        },
            el('td', {},
                el('div', { class: 'mpz-ct-title-cell' },
                    el('span', {
                        class: 'mpz-ct-title',
                        title: item.title
                    }, item.title || __('(No title)', 'multilingual-press-zone')),
                    el('span', { class: 'mpz-ct-meta' },
                        `ID: ${item.id}`
                    )
                )
            ),
            el('td', {},
                new Badge({ text: statusText, variant: statusVariant }).getElement()
            ),
            el('td', {},
                new Badge({ text: `${translated}/${total}`, variant: badgeVariant }).getElement()
            ),
            el('td', {},
                el('span', { class: 'mpz-ct-date' }, this.formatDate(item.updated_at))
            ),
            el('td', {},
                el('div', { class: 'mpz-table-actions' },
                    Button({
                        label: __('Translate', 'multilingual-press-zone'),
                        variant: 'primary',
                        size: 'sm',
                        onClick: (e) => {
                            e.stopPropagation();
                            this.openEditModal(item);
                        }
                    }),
                    Button({
                        label: __('Generate', 'multilingual-press-zone'),
                        variant: 'secondary',
                        size: 'sm',
                        onClick: (e) => {
                            e.stopPropagation();
                            this.handleGenerate(item);
                        }
                    })
                )
            )
        );

        return row;
    }

    renderPagination() {
        if (!this.paginationContainer) return;
        clear(this.paginationContainer);

        if (this.totalPages <= 1) return;

        this.paginationContainer.appendChild(
            el('div', { class: 'mpz-pagination' },
                Button({
                    label: __('Previous', 'multilingual-press-zone'),
                    variant: 'ghost',
                    size: 'sm',
                    onClick: () => this.goToPage(this.currentPage - 1),
                    disabled: this.currentPage === 1
                }),
                el('span', { class: 'mpz-pagination__info' },
                    `${__('Page', 'multilingual-press-zone')} ${this.currentPage} ${__('of', 'multilingual-press-zone')} ${this.totalPages}`
                ),
                Button({
                    label: __('Next', 'multilingual-press-zone'),
                    variant: 'ghost',
                    size: 'sm',
                    onClick: () => this.goToPage(this.currentPage + 1),
                    disabled: this.currentPage === this.totalPages
                })
            )
        );
    }

    goToPage(page) {
        if (page < 1 || page > this.totalPages) return;
        this.currentPage = page;
        this.loadContent();
    }

    _onFilterChange() {
        this.currentPage = 1;
        this.loadContent();
    }

    formatDate(dateString) {
        if (!dateString) return '-';

        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return __('Just now', 'multilingual-press-zone');
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;

        return date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    }

    // --- Edit Modal ---

    async openEditModal(item) {
        try {
            const response = await API.get(`/translations/content/${item.id}`);
            this.editContentData = response.data;

            if (!this.editContentData || !this.editContentData.language_details) {
                Toast.error(__('Failed to load content details', 'multilingual-press-zone'));
                return;
            }

            // Default to first active untranslated language, then any untranslated, then first non-source
            const nonSource = this.editContentData.language_details.filter(l => !l.is_source);
            const firstUntranslated = nonSource.find(l => l.translation_status === 'not_translated' && l.is_active !== false)
                || nonSource.find(l => l.translation_status === 'not_translated');
            this.editModalActiveTab = firstUntranslated
                ? firstUntranslated.language_code
                : (nonSource[0]?.language_code || this.editContentData.language_details[0]?.language_code);

            // Reset generate state
            this._generating = false;
            this._generatingLang = null;
            this._characterEstimates = {};
            this.translateAllInProgress = false;
            this.translateAllProgress = { total: 0, completed: 0, failed: 0, currentLang: null, results: [] };
            this.translateAllProgressBar = null;

            this.modal = new Modal({
                title: __('Edit Translation', 'multilingual-press-zone'),
                width: '900px',
                id: 'mpz-content-edit-modal'
            });

            const content = this.renderEditModalContent();
            const tabs = this.renderEditModalTabs();
            const actions = this.renderEditModalActions();

            this.modal.render(content, actions, tabs);
            this.modal.open();

            // Non-blocking: fetch character estimates for active untranslated languages only
            if (window.multilingualPressZone?.translate?.available) {
                const nonSource = this.editContentData.language_details.filter(l => !l.is_source);
                const untranslatedLangs = nonSource
                    .filter(l => l.translation_status === 'not_translated' && l.is_active !== false)
                    .map(l => l.language_code);
                if (untranslatedLangs.length > 0) {
                    this.fetchCharacterEstimates(this.editContentData.id, untranslatedLangs);
                }
            }
        } catch (error) {
            Toast.error(error?.message || __('Failed to load content details', 'multilingual-press-zone'));
        }
    }

    renderEditModalTabs() {
        if (!this.editContentData?.language_details) return null;

        return this.editContentData.language_details
            .filter(lang => !lang.is_source)
            .map(lang => {
                const isActive = lang.language_code === this.editModalActiveTab;
                const isTranslated = lang.translation_status === 'translated';
                const isInactive = lang.is_active === false;
                const flag = this.getFlagEmoji(lang.flag_code || lang.language_code);

                const classes = [
                    'presszone-multilingual-tabs__item',
                    isActive ? 'presszone-multilingual-tabs__item--active' : '',
                    isInactive ? 'presszone-multilingual-tabs__item--inactive' : '',
                ].filter(Boolean).join(' ');

                return el('button', {
                    type: 'button',
                    role: 'tab',
                    'aria-selected': isActive ? 'true' : 'false',
                    class: classes,
                    'data-tab': lang.language_code,
                    onclick: (e) => {
                        e.preventDefault();
                        this.editModalActiveTab = lang.language_code;
                        this.modal.updateContent(
                            this.renderEditModalContent(),
                            this.renderEditModalTabs()
                        );
                        this.modal.updateActions(this.renderEditModalActions());
                    }
                },
                    el('span', {}, `${flag} ${lang.native_name || lang.name}`),
                    isTranslated ? el('span', {
                        class: 'mpz-st-tab-check',
                        'aria-label': __('Translated', 'multilingual-press-zone')
                    }, ' \u2713') : null,
                    isInactive ? el('span', {
                        class: 'mpz-st-tab-inactive-badge',
                        'aria-label': __('Inactive', 'multilingual-press-zone')
                    }, __('inactive', 'multilingual-press-zone')) : null
                );
            });
    }

    renderEditModalContent() {
        if (!this.editContentData) return el('div');

        const lang = this.editContentData.language_details.find(
            l => l.language_code === this.editModalActiveTab
        );

        const sourceDetail = this.editContentData.language_details.find(l => l.is_source);
        const originalTitle = this.editContentData.title || '';
        const originalContent = this.editContentData.content || '';
        const originalExcerpt = this.editContentData.excerpt || '';

        const translatedTitle = lang?.translated_title || '';
        const translatedExcerpt = lang?.translated_excerpt || '';
        const status = lang?.translation_status || 'not_translated';
        const translatedPostId = lang?.element_id;

        // Determine text directions
        const sourceDir = sourceDetail?.text_direction === 'rtl' ? 'rtl' : 'ltr';
        const targetDir = lang?.text_direction === 'rtl' ? 'rtl' : 'ltr';

        const translationLabel = __('Translation', 'multilingual-press-zone') +
            ` (${lang?.native_name || lang?.name || ''})`;

        // Left side: original content (read-only)
        const originalSide = el('div', { class: 'mpz-st-edit-grid__original' },
            el('label', { class: 'mpz-st-edit-label' },
                __('Original', 'multilingual-press-zone') +
                (sourceDetail ? ` (${sourceDetail.native_name || sourceDetail.name})` : '')
            ),
            el('div', { class: 'mpz-st-edit-grid__original-text mpz-ct-original-fields', dir: sourceDir },
                el('div', { class: 'mpz-ct-field-group' },
                    el('span', { class: 'mpz-ct-field-label' }, __('Title', 'multilingual-press-zone')),
                    el('div', { class: 'mpz-ct-field-value mpz-ct-field-value--title' }, originalTitle)
                ),
                el('div', { class: 'mpz-ct-field-group' },
                    el('span', { class: 'mpz-ct-field-label' }, __('Content', 'multilingual-press-zone')),
                    el('div', { class: 'mpz-ct-field-value mpz-ct-field-value--content' }, originalContent)
                ),
                originalExcerpt ? el('div', { class: 'mpz-ct-field-group' },
                    el('span', { class: 'mpz-ct-field-label' }, __('Excerpt', 'multilingual-press-zone')),
                    el('div', { class: 'mpz-ct-field-value' }, originalExcerpt)
                ) : null
            )
        );

        // Right side: editable translation fields (or generating spinner)
        let translationSide;

        if (this._generating && this._generatingLang === this.editModalActiveTab) {
            translationSide = el('div', { class: 'mpz-st-edit-grid__translation' },
                el('label', { class: 'mpz-st-edit-label' }, translationLabel),
                el('div', { class: 'mpz-ct-generating' },
                    el('div', { class: 'mpz-ct-generating__spinner' }),
                    el('p', { class: 'mpz-ct-generating__text' },
                        __('Generating translation...', 'multilingual-press-zone'))
                )
            );
        } else {
            // Build editable fields for both new and existing translations
            const titleInput = el('input', {
                type: 'text',
                id: 'mpz-ct-translation-title',
                class: 'mpz-ct-translation-input presszone-multilingual-input--settings',
                dir: targetDir,
                placeholder: __('Enter translated title...', 'multilingual-press-zone'),
            });
            titleInput.value = translatedTitle;

            const contentTextarea = el('textarea', {
                id: 'mpz-ct-translation-content',
                class: 'mpz-ct-translation-textarea',
                dir: targetDir,
                rows: '10',
                placeholder: __('Enter translated content...', 'multilingual-press-zone'),
            });
            contentTextarea.value = translatedExcerpt;

            translationSide = el('div', { class: 'mpz-st-edit-grid__translation' },
                el('label', { class: 'mpz-st-edit-label' }, translationLabel),
                el('div', { class: 'mpz-ct-translation-fields' },
                    el('div', { class: 'mpz-ct-field-group' },
                        el('label', {
                            class: 'mpz-ct-field-label',
                            for: 'mpz-ct-translation-title'
                        }, __('Title', 'multilingual-press-zone')),
                        titleInput
                    ),
                    el('div', { class: 'mpz-ct-field-group' },
                        el('label', {
                            class: 'mpz-ct-field-label',
                            for: 'mpz-ct-translation-content'
                        }, __('Content', 'multilingual-press-zone')),
                        contentTextarea
                    )
                ),
                translatedPostId ? el('div', { class: 'mpz-ct-edit-link' },
                    el('a', {
                        href: `/wp-admin/post.php?post=${translatedPostId}&action=edit`,
                        target: '_blank',
                        rel: 'noopener noreferrer',
                        class: 'mpz-ct-edit-link__anchor'
                    }, __('Edit in WordPress editor', 'multilingual-press-zone') + ' \u2197')
                ) : null
            );
        }

        return el('div', { class: 'mpz-st-edit-layout' },
            el('div', { class: 'mpz-st-edit-grid' },
                originalSide,
                translationSide
            ),
            el('div', { class: 'mpz-st-edit-meta' },
                el('span', {}, `${__('Type:', 'multilingual-press-zone')} ${this.editContentData.post_type}`),
                el('span', {}, `${__('Status:', 'multilingual-press-zone')} `),
                Badge.fromStatus(status === 'not_translated' ? 'pending' : status).getElement(),
                el('span', {}, lang?.updated_at
                    ? `${__('Last Updated:', 'multilingual-press-zone')} ${new Date(lang.updated_at).toLocaleString()}`
                    : __('Last Updated: Never', 'multilingual-press-zone')
                )
            )
        );
    }

    renderEditModalActions() {
        // During translate-all — no action buttons
        if (this.translateAllInProgress) {
            return null;
        }

        const lang = this.editContentData?.language_details?.find(
            l => l.language_code === this.editModalActiveTab
        );

        const actions = [
            Button({
                label: __('Close', 'multilingual-press-zone'),
                variant: 'ghost',
                onClick: () => this.modal.close()
            }),
        ];

        if (window.multilingualPressZone?.translate?.available) {
            const missingLangs = this.getMissingLanguages();
            const estimate = this._characterEstimates?.[lang?.language_code];
            const charLabel = estimate ? ` (~${estimate.estimated_characters} chars)` : '';

            // "Translate All (N)" when 2+ languages are missing
            if (missingLangs.length > 1) {
                actions.push(Button({
                    label: __('Translate All (%d)', 'multilingual-press-zone').replace('%d', missingLangs.length),
                    variant: 'primary',
                    onClick: () => this.translateAllMissing()
                }));
                actions.push(Button({
                    label: this._generating
                        ? __('Generating...', 'multilingual-press-zone')
                        : __('Generate', 'multilingual-press-zone') + charLabel,
                    variant: 'secondary',
                    disabled: this._generating,
                    onClick: () => this.generateTranslation(lang.language_code)
                }));
            } else {
                actions.push(Button({
                    label: this._generating
                        ? __('Generating...', 'multilingual-press-zone')
                        : __('Generate', 'multilingual-press-zone') + charLabel,
                    variant: 'primary',
                    disabled: this._generating,
                    onClick: () => this.generateTranslation(lang.language_code)
                }));
            }
        }

        // Save button — always available for manual editing
        actions.push(Button({
            label: this._saving
                ? __('Saving...', 'multilingual-press-zone')
                : __('Save', 'multilingual-press-zone'),
            variant: 'secondary',
            disabled: this._saving,
            onClick: () => this.saveContentTranslation()
        }));

        return actions;
    }

    handleGenerate(item) {
        if (window.multilingualPressZone?.translate?.available) {
            this.openEditModal(item);
        } else {
            showTranslatePromoModal();
        }
    }

    async generateTranslation(targetLang) {
        if (!this.editContentData || this._generating) return;

        this._generating = true;
        this._generatingLang = targetLang;

        // Update modal UI to show generating state
        this.modal.updateContent(
            this.renderEditModalContent(),
            this.renderEditModalTabs()
        );
        this.modal.updateActions(this.renderEditModalActions());

        try {
            await API.post('/translations/generate', {
                content_id: this.editContentData.id,
                target_lang: targetLang,
                content_type: this.editContentData.post_type,
            });

            Toast.success(__('Translation generated successfully', 'multilingual-press-zone'));

            // Refresh content data
            const response = await API.get(`/translations/content/${this.editContentData.id}`);
            this.editContentData = response.data;

            // Update table row progress
            this.updateRowProgress(this.editContentData);
        } catch (error) {
            Toast.error(error?.message || __('Failed to generate translation', 'multilingual-press-zone'));
        } finally {
            this._generating = false;
            this._generatingLang = null;

            // Update modal with results
            this.modal.updateContent(
                this.renderEditModalContent(),
                this.renderEditModalTabs()
            );
            this.modal.updateActions(this.renderEditModalActions());
        }
    }

    async fetchCharacterEstimates(contentId, targetLangs) {
        try {
            const result = await API.post('/translations/estimate-characters', {
                content_id: contentId,
                target_langs: targetLangs,
            });

            if (result?.data?.estimates) {
                this._characterEstimates = result.data.estimates;
                // Refresh modal actions to show character counts
                if (this.modal?.modalEl) {
                    this.modal.updateActions(this.renderEditModalActions());
                }
            }
        } catch (error) {
            // Non-blocking — silently ignore estimate failures
        }
    }

    async queueTranslation(targetLanguageCode) {
        if (!this.editContentData) return;

        try {
            Toast.info(__('Queuing translation job...', 'multilingual-press-zone'));

            // Resolve source language — fall back to the default language if the post
            // has no translation record yet (source_language will be empty in that case)
            const sourceLang = this.editContentData.source_language
                || this.languages.find(l => l.is_default || l.isDefault)?.code
                || 'en';

            await API.post('/translation-jobs', {
                contentId: this.editContentData.id,
                sourceLang,
                targetLang: targetLanguageCode,
                contentType: this.editContentData.post_type,
                immediate: true
            });

            Toast.success(__('Translation job queued successfully', 'multilingual-press-zone'));

            // Refresh modal data
            const response = await API.get(`/translations/content/${this.editContentData.id}`);
            this.editContentData = response.data;

            // Advance to next untranslated language
            const nonSource = this.editContentData.language_details.filter(l => !l.is_source);
            const nextUntranslated = nonSource.find(
                l => l.language_code !== this.editModalActiveTab && l.translation_status === 'not_translated'
            );
            if (nextUntranslated) {
                this.editModalActiveTab = nextUntranslated.language_code;
            }

            // Update modal content
            this.modal.updateContent(
                this.renderEditModalContent(),
                this.renderEditModalTabs()
            );

            // Update table row progress
            this.updateRowProgress(this.editContentData);
        } catch (error) {
            Toast.error(error?.message || __('Failed to queue translation', 'multilingual-press-zone'));
        }
    }

    async saveContentTranslation() {
        if (!this.editContentData || this._saving) return;

        const titleInput = document.getElementById('mpz-ct-translation-title');
        const contentTextarea = document.getElementById('mpz-ct-translation-content');

        if (!titleInput) return;

        const title = titleInput.value.trim();
        if (!title) {
            Toast.error(__('Title is required', 'multilingual-press-zone'));
            titleInput.focus();
            return;
        }

        this._saving = true;
        this.modal.updateActions(this.renderEditModalActions());

        try {
            const response = await API.put(
                `/translations/content/${this.editContentData.id}`,
                {
                    language_code: this.editModalActiveTab,
                    title: title,
                    content: contentTextarea ? contentTextarea.value : '',
                    excerpt: '',
                }
            );

            Toast.success(__('Translation saved', 'multilingual-press-zone'));

            // Update local data from response
            this.editContentData = response.data;

            // Update table row progress
            this.updateRowProgress(this.editContentData);

            // Advance to next untranslated language
            const nonSource = this.editContentData.language_details.filter(l => !l.is_source);
            const nextUntranslated = nonSource.find(
                l => l.language_code !== this.editModalActiveTab &&
                     l.translation_status === 'not_translated' &&
                     l.is_active !== false
            );
            if (nextUntranslated) {
                this.editModalActiveTab = nextUntranslated.language_code;
            }

            // Re-render modal content
            this.modal.updateContent(
                this.renderEditModalContent(),
                this.renderEditModalTabs()
            );
            this.modal.updateActions(this.renderEditModalActions());
        } catch (error) {
            Toast.error(error?.message || __('Failed to save translation', 'multilingual-press-zone'));
        } finally {
            this._saving = false;
            this.modal.updateActions(this.renderEditModalActions());
        }
    }

    updateRowProgress(contentData) {
        if (!contentData?.translations) return;

        // Update in-memory items
        const idx = this.items.findIndex(i => i.id === contentData.id);
        if (idx !== -1) {
            this.items[idx].translations = contentData.translations;
        }

        // Update DOM row
        const row = this.tableContainer?.querySelector(`tr[data-content-id="${contentData.id}"]`);
        if (row) {
            const progressTd = row.querySelectorAll('td')[2]; // 3rd column = progress
            if (progressTd) {
                const { translated, total } = contentData.translations;
                const progressPct = total > 0 ? Math.round((translated / total) * 100) : 0;
                let badgeVariant = 'default';
                if (progressPct === 100) badgeVariant = 'success';
                else if (progressPct > 0) badgeVariant = 'warning';

                clear(progressTd);
                progressTd.appendChild(new Badge({ text: `${translated}/${total}`, variant: badgeVariant }).getElement());
            }
        }
    }

    // =========================================================================
    // Translate All (multi-language within modal)
    // =========================================================================

    getMissingLanguages() {
        if (!this.editContentData?.language_details) return [];
        return this.editContentData.language_details
            .filter(l => !l.is_source && l.translation_status === 'not_translated')
            .map(l => l.language_code);
    }

    async translateAllMissing() {
        if (!this.editContentData) return;
        const missingLangs = this.getMissingLanguages();
        if (missingLangs.length === 0) return;

        this.translateAllInProgress = true;
        this.translateAllProgress = {
            total: missingLangs.length,
            completed: 0,
            failed: 0,
            currentLang: missingLangs[0],
            results: []
        };

        this.renderTranslateAllProgressView();

        for (const lang of missingLangs) {
            this.translateAllProgress.currentLang = lang;
            this.updateTranslateAllProgressLabel();

            try {
                await API.post('/translations/generate', {
                    content_id: this.editContentData.id,
                    target_lang: lang,
                    content_type: this.editContentData.post_type,
                });
                this.translateAllProgress.completed++;
                this.translateAllProgress.results.push({ lang, status: 'completed' });

                // Mark locally as translated for tab indicator
                const langDetail = this.editContentData.language_details.find(l => l.language_code === lang);
                if (langDetail) {
                    langDetail.translation_status = 'translated';
                }
            } catch (error) {
                this.translateAllProgress.failed++;
                this.translateAllProgress.results.push({ lang, status: 'failed', error: error.message });

                const msg = (error.message || '').toLowerCase();
                if (msg.includes('license') || msg.includes('quota') || msg.includes('credits') || msg.includes('limit')) {
                    Toast.error(error.message);
                    break;
                }
            }

            // Update progress bar value (bar stays in the same DOM node — no re-render)
            if (this.translateAllProgressBar) {
                this.translateAllProgressBar.setValue(
                    this.translateAllProgress.completed + this.translateAllProgress.failed
                );
            }

            // Refresh only tabs to show per-language checkmarks; reuse the same content element
            if (this.modal?.isOpen && this._translateAllProgressEl) {
                this.modal.updateContent(
                    this._translateAllProgressEl,
                    this.renderTranslateAllTabs()
                );
            }
        }

        this.translateAllInProgress = false;

        // Refresh content data and update table row
        try {
            const response = await API.get(`/translations/content/${this.editContentData.id}`);
            this.editContentData = response.data;
            this.updateRowProgress(this.editContentData);
        } catch (e) { /* non-blocking */ }

        // Restore normal edit modal so user can see the translations immediately
        this._translateAllProgressEl = null;
        const firstTranslated = this.translateAllProgress.results.find(r => r.status === 'completed');
        if (firstTranslated) {
            this.editModalActiveTab = firstTranslated.lang;
        }
        if (this.modal?.isOpen) {
            this.modal.updateContent(
                this.renderEditModalContent(),
                this.renderEditModalTabs()
            );
            this.modal.updateActions(this.renderEditModalActions());
        }
    }

    renderTranslateAllProgressView() {
        if (!this.modal?.isOpen) return;

        // Store the element reference so the loop can reuse it (avoids detaching the ProgressBar)
        this._translateAllProgressEl = this.renderTranslateAllProgressContent();
        this.modal.updateContent(this._translateAllProgressEl, this.renderTranslateAllTabs());
        this.modal.updateActions(null);

        const barContainer = this._translateAllProgressEl.querySelector('.mpz-translate-all-progress__bar');
        if (barContainer) {
            this.translateAllProgressBar = new ProgressBar({
                container: barContainer,
                value: 0,
                max: this.translateAllProgress.total,
                animated: true,
                showPercentage: true,
                status: 'in-progress'
            });
        }
    }

    renderTranslateAllProgressContent() {
        const { currentLang, completed, failed, total } = this.translateAllProgress;
        const langDetail = this.editContentData?.language_details?.find(l => l.language_code === currentLang);
        const langName = langDetail?.native_name || langDetail?.name || (currentLang ? currentLang.toUpperCase() : '');
        const current = Math.min(completed + failed + 1, total);

        return el('div', { class: 'mpz-bulk-progress-container' },
            el('div', {
                class: 'mpz-bulk-progress-container__label',
                'data-translate-all-label': 'true'
            },
                __('Translating to %1$s (%2$d/%3$d)', 'multilingual-press-zone')
                    .replace('%1$s', langName)
                    .replace('%2$d', current)
                    .replace('%3$d', total)
            ),
            el('div', { class: 'mpz-translate-all-progress__bar mpz-bulk-progress-container__bar' })
        );
    }

    updateTranslateAllProgressLabel() {
        if (!this.modal?.isOpen) return;
        const label = this.modal.modalEl?.querySelector('[data-translate-all-label]');
        if (!label) return;

        const { currentLang, completed, failed, total } = this.translateAllProgress;
        const langDetail = this.editContentData?.language_details?.find(l => l.language_code === currentLang);
        const langName = langDetail?.native_name || langDetail?.name || currentLang.toUpperCase();
        const current = Math.min(completed + failed + 1, total);

        label.textContent = __('Translating to %1$s (%2$d/%3$d)', 'multilingual-press-zone')
            .replace('%1$s', langName)
            .replace('%2$d', current)
            .replace('%3$d', total);
    }

    renderTranslateAllTabs() {
        if (!this.editContentData?.language_details) return [];

        const resultMap = new Map(
            this.translateAllProgress.results.map(r => [r.lang, r.status])
        );

        return this.editContentData.language_details
            .filter(l => !l.is_source)
            .map(lang => {
                const code = lang.language_code;
                const flag = this.getFlagEmoji(lang.flag_code || code);
                const isInactive = lang.is_active === false;
                const translateAllResult = resultMap.get(code);
                const isCurrent = code === this.translateAllProgress.currentLang && !translateAllResult;

                const classes = [
                    'presszone-multilingual-tabs__item',
                    isCurrent ? 'presszone-multilingual-tabs__item--active' : '',
                    isInactive ? 'presszone-multilingual-tabs__item--inactive' : ''
                ].filter(Boolean).join(' ');

                let statusIndicator = null;
                if (translateAllResult === 'completed' || lang.translation_status === 'translated') {
                    statusIndicator = el('span', {
                        class: 'mpz-st-tab-check',
                        'aria-label': __('Translated', 'multilingual-press-zone')
                    }, ' \u2713');
                } else if (translateAllResult === 'failed') {
                    statusIndicator = el('span', {
                        class: 'mpz-st-tab-check mpz-st-tab-check--failed',
                        'aria-label': __('Failed', 'multilingual-press-zone')
                    }, ' \u2717');
                } else if (isInactive) {
                    statusIndicator = el('span', {
                        class: 'mpz-st-tab-inactive-badge',
                        'aria-label': __('Inactive', 'multilingual-press-zone')
                    }, __('inactive', 'multilingual-press-zone'));
                }

                return el('button', {
                    type: 'button',
                    role: 'tab',
                    'aria-selected': isCurrent ? 'true' : 'false',
                    class: classes,
                    disabled: true
                },
                    el('span', {}, `${flag} ${lang.native_name || lang.name}`),
                    statusIndicator
                );
            });
    }

    renderTranslateAllCompletion() {
        if (!this.modal?.isOpen) return;

        const { completed, failed, total, results } = this.translateAllProgress;
        const hasFailures = failed > 0;
        const iconText = hasFailures ? '\u26A0' : '\u2713';
        const iconClass = hasFailures
            ? 'tpz-modal-progress__icon tpz-modal-progress__icon--error'
            : 'tpz-modal-progress__icon';

        const summaryText = hasFailures
            ? __('%1$d of %2$d languages translated', 'multilingual-press-zone')
                .replace('%1$d', completed)
                .replace('%2$d', total)
            : __('All %d languages translated', 'multilingual-press-zone')
                .replace('%d', completed);

        const resultItems = results.map(r => {
            const langDetail = this.editContentData?.language_details?.find(l => l.language_code === r.lang);
            const langName = langDetail?.native_name || langDetail?.name || r.lang.toUpperCase();
            const flag = this.getFlagEmoji(langDetail?.flag_code || r.lang);
            const isOk = r.status === 'completed';
            const editPostId = langDetail?.element_id;

            return el('div', { class: 'mpz-translate-all-result' },
                el('span', { class: 'mpz-translate-all-result__lang' }, `${flag} ${langName}`),
                el('span', {
                    class: `mpz-translate-all-result__status mpz-translate-all-result__status--${isOk ? 'ok' : 'fail'}`
                }, isOk ? '\u2713' : '\u2717'),
                isOk && editPostId
                    ? el('a', {
                        class: 'mpz-translate-all-result__link',
                        href: `/wp-admin/post.php?post=${editPostId}&action=edit`,
                        target: '_blank',
                        rel: 'noopener noreferrer'
                    }, __('Edit', 'multilingual-press-zone') + ' \u2197')
                    : null,
                !isOk ? el('span', { class: 'mpz-translate-all-result__error' }, r.error || '') : null
            );
        });

        const content = el('div', { class: 'tpz-modal-progress tpz-modal-progress--done' },
            el('div', { class: iconClass }, iconText),
            el('h3', { class: 'tpz-modal-progress__title' }, summaryText),
            el('div', { class: 'mpz-translate-all-results' }, ...resultItems)
        );

        this.modal.updateContent(content, this.renderTranslateAllTabs());
        this.modal.updateActions(
            Button({
                label: __('Close', 'multilingual-press-zone'),
                variant: 'primary',
                onClick: () => this.modal.close()
            })
        );
    }

    getFlagEmoji(code) {
        if (!code || code.length !== 2) return '\uD83C\uDF10'; // globe
        const codePoints = code
            .toUpperCase()
            .split('')
            .map(char => 127397 + char.charCodeAt(0));
        return String.fromCodePoint(...codePoints);
    }

    destroy() {
        this._destroyed = true;
        if (this.modal) {
            this.modal.close();
            this.modal = null;
        }
        this.items = [];
        clear(this.container);
    }
}
